/* ==========================================================================
   MERIDIAN — theme.js
   Zero-dependency interaction + motion layer.

   Why no GSAP: the brief asks for GSAP-grade motion AND Lighthouse 95+.
   A third-party animation library costs ~70KB gzipped and blocks the main
   thread on a content site whose job is to rank. Everything below is done
   with IntersectionObserver + CSS transitions + rAF, in ~6KB, and honours
   prefers-reduced-motion and the theme's animation settings.

   Modules
     1. Reveal on scroll        [data-reveal]
     2. Count-up numerals       [data-count-to]
     3. Parallax layers         [data-parallax]
     4. Header state + drawer + search
     5. Accordions              [data-accordion]
     6. Tabs                    [data-tabs]
     7. Horizontal rails        [data-rail]
     8. Funding calculator      [data-calc]
     9. Mobile conversion bar
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  var animOn = root.classList.contains('anim');
  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };

  /* ---------------------------------------------------------------- 1. Reveal */
  function initReveal() {
    var items = $$('[data-reveal]');
    if (!items.length) return;

    if (!animOn || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var delay = parseInt(el.getAttribute('data-reveal-delay') || '0', 10);
        // Stagger children of a group without hand-authoring delays.
        el.style.setProperty('--d', delay + 'ms');
        el.classList.add('is-in');
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    items.forEach(function (el) { io.observe(el); });
  }

  /* -------------------------------------------------------------- 2. Counters */
  function formatNumber(value, decimals) {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }

  function runCount(el) {
    var target = parseFloat(el.getAttribute('data-count-to'));
    if (isNaN(target)) return;
    var decimals = parseInt(el.getAttribute('data-count-decimals') || '0', 10);
    var duration = parseInt(el.getAttribute('data-count-duration') || '1600', 10);

    if (!animOn) { el.textContent = formatNumber(target, decimals); return; }

    var start = null;
    function step(ts) {
      if (start === null) start = ts;
      var p = Math.min((ts - start) / duration, 1);
      // easeOutExpo — fast settle, no bounce. Numbers should land, not wobble.
      var eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
      el.textContent = formatNumber(target * eased, decimals);
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function initCounters() {
    var nums = $$('[data-count-to]');
    if (!nums.length) return;

    if (!('IntersectionObserver' in window)) { nums.forEach(runCount); return; }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        runCount(entry.target);
        io.unobserve(entry.target);
      });
    }, { threshold: 0.4 });

    nums.forEach(function (el) { io.observe(el); });
  }

  /* -------------------------------------------------------------- 3. Parallax */
  function initParallax() {
    var layers = $$('[data-parallax]');
    if (!animOn || !layers.length || window.matchMedia('(max-width: 749px)').matches) return;

    var ticking = false;
    function update() {
      var vh = window.innerHeight;
      layers.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        var speed = parseFloat(el.getAttribute('data-parallax')) || 0.12;
        var offset = (rect.top + rect.height / 2 - vh / 2) * speed * -1;
        el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
      });
      ticking = false;
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ---------------------------------------------------- 4. Header, drawer, search */
  function initHeader() {
    var header = $('[data-header]');
    if (!header) return;

    var lastY = 0;
    var ticking = false;
    function update() {
      var y = window.scrollY;
      header.classList.toggle('is-stuck', y > 8);
      lastY = y;
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });
    update();
  }

  function trapFocus(container, e) {
    var focusables = $$('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])', container)
      .filter(function (el) { return el.offsetParent !== null; });
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function initOverlays() {
    var drawer = $('[data-drawer]');
    var scrim = $('[data-scrim]');
    var search = $('[data-search-overlay]');
    var body = document.body;
    var openEl = null;
    var lastFocused = null;

    function open(el) {
      if (!el) return;
      lastFocused = document.activeElement;
      openEl = el;
      el.setAttribute('aria-hidden', 'false');
      if (scrim && el === drawer) scrim.setAttribute('aria-hidden', 'false');
      body.classList.add('is-locked');
      var focusTarget = el.querySelector('input, a, button');
      if (focusTarget) setTimeout(function () { focusTarget.focus(); }, 60);
    }

    function close() {
      if (!openEl) return;
      openEl.setAttribute('aria-hidden', 'true');
      if (scrim) scrim.setAttribute('aria-hidden', 'true');
      body.classList.remove('is-locked');
      if (lastFocused) lastFocused.focus();
      openEl = null;
    }

    $$('[data-open="drawer"]').forEach(function (btn) {
      btn.addEventListener('click', function () { open(drawer); });
    });
    $$('[data-open="search"]').forEach(function (btn) {
      btn.addEventListener('click', function () { open(search); });
    });
    $$('[data-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });
    if (scrim) scrim.addEventListener('click', close);
    if (search) {
      search.addEventListener('click', function (e) { if (e.target === search) close(); });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && openEl) close();
      if (e.key === 'Tab' && openEl) trapFocus(openEl, e);
      // Cmd/Ctrl+K opens search — a small courtesy for people who read a lot.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openEl ? close() : open(search);
      }
    });

    // Drawer sub-menu accordions
    $$('[data-drawer-toggle]', drawer).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        btn.setAttribute('aria-expanded', String(!expanded));
        if (panel) panel.setAttribute('data-open', String(!expanded));
      });
    });
  }

  /* ------------------------------------------------------------ 5. Accordions */
  function initAccordions() {
    $$('[data-accordion]').forEach(function (group) {
      var single = group.getAttribute('data-accordion') === 'single';
      var buttons = $$('[data-qa-toggle]', group);

      buttons.forEach(function (btn) {
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (!panel) return;

        btn.addEventListener('click', function () {
          var isOpen = btn.getAttribute('aria-expanded') === 'true';

          if (single && !isOpen) {
            buttons.forEach(function (other) {
              if (other === btn) return;
              var otherPanel = document.getElementById(other.getAttribute('aria-controls'));
              other.setAttribute('aria-expanded', 'false');
              if (otherPanel) otherPanel.style.maxHeight = '0px';
            });
          }

          btn.setAttribute('aria-expanded', String(!isOpen));
          panel.style.maxHeight = isOpen ? '0px' : panel.scrollHeight + 'px';
        });

        // Keep an open panel correctly sized when the viewport reflows.
        window.addEventListener('resize', function () {
          if (btn.getAttribute('aria-expanded') === 'true') {
            panel.style.maxHeight = panel.scrollHeight + 'px';
          }
        }, { passive: true });
      });
    });
  }

  /* ------------------------------------------------------------------ 6. Tabs */
  function initTabs() {
    $$('[data-tabs]').forEach(function (group) {
      var tabs = $$('[role="tab"]', group);
      var panels = $$('[role="tabpanel"]', group);

      function activate(index) {
        tabs.forEach(function (tab, i) {
          var selected = i === index;
          tab.setAttribute('aria-selected', String(selected));
          tab.setAttribute('tabindex', selected ? '0' : '-1');
        });
        panels.forEach(function (panel, i) {
          panel.setAttribute('data-active', String(i === index));
        });
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { activate(i); });
        tab.addEventListener('keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = (i + 1) % tabs.length;
          if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
          if (e.key === 'Home') next = 0;
          if (e.key === 'End') next = tabs.length - 1;
          if (next === null) return;
          e.preventDefault();
          activate(next);
          tabs[next].focus();
        });
      });
    });
  }

  /* ----------------------------------------------------------------- 7. Rails */
  function initRails() {
    $$('[data-rail-wrap]').forEach(function (wrap) {
      var rail = $('[data-rail]', wrap);
      var prev = $('[data-rail-prev]', wrap);
      var next = $('[data-rail-next]', wrap);
      if (!rail) return;

      function step() {
        var first = rail.firstElementChild;
        if (!first) return rail.clientWidth * 0.8;
        var gap = parseFloat(getComputedStyle(rail).columnGap || '24') || 24;
        return first.getBoundingClientRect().width + gap;
      }

      function sync() {
        if (!prev || !next) return;
        var max = rail.scrollWidth - rail.clientWidth - 2;
        prev.disabled = rail.scrollLeft <= 2;
        next.disabled = rail.scrollLeft >= max;
      }

      if (prev) prev.addEventListener('click', function () { rail.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { rail.scrollBy({ left: step(), behavior: 'smooth' }); });
      rail.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      sync();
    });
  }

  /* ------------------------------------------------------------ 8. Calculator */
  function initCalculator() {
    $$('[data-calc]').forEach(function (calc) {
      var amount = $('[data-calc-amount]', calc);
      var term = $('[data-calc-term]', calc);
      var rate = $('[data-calc-rate]', calc);
      var outAmount = $('[data-out-amount]', calc);
      var outTerm = $('[data-out-term]', calc);
      var outPayment = $('[data-out-payment]', calc);
      var outTotal = $('[data-out-total]', calc);
      var outCost = $('[data-out-cost]', calc);
      if (!amount || !term || !outPayment) return;

      var usd = function (n) {
        return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
      };

      function fill(input) {
        var min = parseFloat(input.min) || 0;
        var max = parseFloat(input.max) || 100;
        var pct = ((parseFloat(input.value) - min) / (max - min)) * 100;
        input.style.setProperty('--fill', pct + '%');
      }

      function compute() {
        var principal = parseFloat(amount.value);
        var months = parseFloat(term.value);
        var factor = rate ? parseFloat(rate.value) : 1.18;

        // Short-term business funding is quoted on a factor rate, not APR.
        // Total payback = principal x factor. This mirrors how an MCA or
        // short-term term-loan offer is actually presented to an owner.
        var total = principal * factor;
        var payment = total / months;
        var cost = total - principal;

        if (outAmount) outAmount.textContent = usd(principal);
        if (outTerm) outTerm.textContent = months + ' mo';
        outPayment.textContent = usd(Math.round(payment));
        if (outTotal) outTotal.textContent = usd(Math.round(total));
        if (outCost) outCost.textContent = usd(Math.round(cost));

        fill(amount);
        fill(term);
      }

      [amount, term, rate].forEach(function (input) {
        if (!input) return;
        input.addEventListener('input', compute);
        input.addEventListener('change', compute);
      });
      compute();
    });
  }

  /* -------------------------------------------------------- 9. Mobile CTA bar */
  function initMobileCta() {
    var bar = $('[data-mobile-cta]');
    if (!bar || bar.getAttribute('data-enabled') !== 'true') return;

    var ticking = false;
    function update() {
      bar.classList.toggle('is-visible', window.scrollY > 640);
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });
    update();
  }

  /* -------------------------------------------------------- Wrap RTE tables */
  function wrapTables() {
    $$('.rte table').forEach(function (table) {
      if (table.parentElement.classList.contains('rte-table-wrap')) return;
      var wrap = document.createElement('div');
      wrap.className = 'rte-table-wrap';
      table.parentNode.insertBefore(wrap, table);
      wrap.appendChild(table);
    });
  }

  function boot() {
    initReveal();
    initCounters();
    initParallax();
    initHeader();
    initOverlays();
    initAccordions();
    initTabs();
    initRails();
    initCalculator();
    initMobileCta();
    wrapTables();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-boot inside the theme editor so sections stay alive after an edit.
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:select', boot);
  document.addEventListener('shopify:block:select', boot);
})();
